import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled, { css } from "styled-components"

import Link from "../utils/Link"
import Image from "../utils/Image"

import Card from "../atoms/Card"
import Box from "../atoms/Box"
import Flex from "../atoms/Flex"
import ProportionalBox from "../atoms/ProportionalBox"
import { Title, Paragraph, Label } from "../atoms/Typography"
import { formatLocalDate } from "../../utils/date"

// switch out with brand colors later, hard coded for now
const UNDERLINE_COLOR = "#176deb"

const StyledThumbnail = styled(Flex)`
  transition: transform 0.2s ease-in-out;

  &:hover {
    ${props =>
      props.zoomOnHover &&
      css`
        transform: scale(1.05);
      `}
  }
`

const underlineStyles = css`
  display: inline;
  text-decoration: none;

  /* spacing + typography */
  line-height: 1.55;
  padding-bottom: 5px;

  /* underline */
  background-image: linear-gradient(
    to right,
    ${UNDERLINE_COLOR},
    ${UNDERLINE_COLOR}
  );
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 0% 2px;

  transition: background-size 300ms cubic-bezier(0.89, 0.03, 0.21, 0.92);
`

const UnderlinePostLabel = styled(Label)`
  ${props => props.$underlineOnHover && underlineStyles}
`

const UnderlineTitle = styled(Title)`
  ${props => props.$underlineOnHover && underlineStyles}
`

const UnderlineDescription = styled(Paragraph)`
  ${props => props.$underlineOnHover && underlineStyles}
`

const PostHoverZone = styled.div`
  height: 100%;

  &:hover
    ${UnderlinePostLabel},
    &:hover
    ${UnderlineTitle},
    &:hover
    ${UnderlineDescription} {
    background-size: 100% 2px;
  }
`

const Post = ({
  title,
  label,
  description,
  thumbnail,
  date,
  link,
  card,
  showDate,
  thumbnailFitType = "fill",
  align,
  imageFieldId,
  zoomOnHover,
  underlineOnHover,
  displayImages,
  postTitleVariant,
}) => {
  const dateLabel = formatLocalDate(date, "MMM d, y")

  return (
    <PostHoverZone>
      <Link aria-label="link to post article" {...link}>
        <Card height="100%" {...card}>
          <Flex height="100%" flexDirection="column">
            {thumbnail && displayImages && (
              <Box height="fit-content">
                <ProportionalBox>
                  <StyledThumbnail
                    zoomOnHover={zoomOnHover}
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    bg="background.1"
                    position="relative"
                  >
                    <Image
                      alternativeText="Post Thumbnail Image"
                      {...thumbnail}
                      style={{
                        position: "absolute",
                        inset: 0,
                        objectFit: "cover",
                      }}
                      imgStyle={{
                        objectFit: "cover",
                        width:
                          thumbnailFitType === "fill" ||
                          thumbnailFitType === "width"
                            ? "100%"
                            : "auto",
                        height:
                          thumbnailFitType === "fill" ||
                          thumbnailFitType === "height"
                            ? "100%"
                            : "auto",
                        margin: "auto",
                      }}
                      imageFieldId={imageFieldId}
                    />
                  </StyledThumbnail>
                </ProportionalBox>
              </Box>
            )}

            <Flex
              px={card?.useCard ? { _: 4, sm: 5 } : 0}
              py={4}
              height="100%"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box textAlign={align}>
                {label && (
                  <Box>
                    <UnderlinePostLabel
                      textAlign={align}
                      $underlineOnHover={underlineOnHover}
                    >
                      {label}
                    </UnderlinePostLabel>
                  </Box>
                )}

                <Box mt={3}>
                  <UnderlineTitle
                    variant={postTitleVariant || "h3"}
                    textAlign={align}
                    $underlineOnHover={underlineOnHover}
                  >
                    {title}
                  </UnderlineTitle>
                </Box>

                {description && (
                  <Box mt={3}>
                    <UnderlineDescription
                      textAlign={align}
                      $underlineOnHover={underlineOnHover}
                    >
                      {description}
                    </UnderlineDescription>
                  </Box>
                )}
              </Box>

              {showDate && dateLabel && (
                <Box textAlign={align}>
                  <Label mt={3} opacity={0.5}>
                    {dateLabel}
                  </Label>
                </Box>
              )}
            </Flex>
          </Flex>
        </Card>
      </Link>
    </PostHoverZone>
  )
}

export default Post

Post.strapiProps = {
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  thumbnail: PropTypes.object.isRequired,
  date: PropTypes.string.isRequired,
  link: PropTypes.shape(Link.strapiProps),
  tag: PropTypes.object,
}

Post.propTypes = {
  ...Post.strapiProps,
  align: PropTypes.oneOf(["left", "center", "right"]),
}

export const query = graphql`
  fragment Post on STRAPI__COMPONENT_MOLECULES_POSTS {
    title
    label
    description
    thumbnail {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(
            formats: [AUTO, WEBP, AVIF]
            width: 600
            layout: CONSTRAINED
          )
        }
      }
    }
    thumbnailFitType
    date
    link {
      ...Link
    }
    tags {
      name
    }
  }
`
